import React, { useEffect, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { useAxiosInstance } from "../../services/Interceptor";
import { setProfileAction } from "../../store/AthleteActions";
import { setScoreConfig } from "../../store/slices/athleteSlice";
import AthleteInformation from "./athlete-information";
import CombineResults from "./combines/athlete-combine-results";
import LoadingSpinner from "../LoadingSpinner";
import AthleteMeasurements from "./measurements/athlete-measurements";
import AthleteCombineButtons from "./combines/athlete-combine-buttons";
import PositionDrills from "./position-drills/positionDrills";
import AthleteAdditionalInfo from "./additional-info/athleteAdditionalInfo";
import InfoModal from "../InfoModal";
import { useResponsiveHeight } from "../../hooks/useResponsiveHeight";
import { getAthleteProfile } from "../../services/AthleteService";
import { useLocation } from "react-router-dom";
import useAuthUser from "react-auth-kit/hooks/useAuthUser";

const SCORE_INFO_TITLE = "Score Info";
const SCORE_INFO_DESCRIPTIONS = {
  default:
    "All of the scores on this page represent the percentile compared to other athletes who have attended any Trackit Verified Combine. This should not be viewed as a grade, but as a way to see how you compare to other athletes.",
  combine:
    "All of the scores on this page represent the percentile compared to other athletes who have attended the same Combine. This should not be viewed as a grade, but as a way to see how you compare to other athletes.",
  university:
    "All of the scores on this page represent the percentile compared to other athletes who have attended the same Combine. This should not be viewed as a grade, but as a way to see how you compare to other athletes.",
};

function getScoreInfoDescription({ isUniversityRole, isCombineLocation }) {
  if (isUniversityRole) return SCORE_INFO_DESCRIPTIONS.university;
  if (isCombineLocation) return SCORE_INFO_DESCRIPTIONS.combine;
  return SCORE_INFO_DESCRIPTIONS.default;
}

export default function AthleteScore({
  userId,
  showOffers,
  showTrackittScore,
  showPositionScore,
  searchEvent,
  showProfile,
  showRank,
  showLikes,
  sharedProfile = false,
  eventId = undefined,
  onFavoritesRefresh,
  isolateProfile = false,
}) {
  const dispatch = useDispatch();
  const profile = useSelector((state) => state.athletes.profile, shallowEqual);
  const loading = useSelector((state) => state.athletes.loading);
  const buttons = useSelector((state) => state.athletes.buttons);
  const location = useLocation();
  const authUser = useAuthUser();
  const apiInstance = useAxiosInstance();
  const [move, setMove] = useState(0);
  const [tab, setTab] = useState(1);
  const [showScoreInfoModal, setShowScoreInfoModal] = useState(false);
  const hasSubscription = useSelector((state) => state.user.hasSubscription);

  const isLocked = sharedProfile && !hasSubscription;
  const { adjustedHeight } = useResponsiveHeight({
    mobileHeight: 280,
    desktopHeight: 480,
  });
  const [localProfile, setLocalProfile] = useState(null);
  const isCombineLocation = location.pathname.includes("/combine");
  const isUniversityRole = authUser?.user?.role === "university";
  const scoreInfoDescription = getScoreInfoDescription({
    isUniversityRole,
    isCombineLocation,
  });
  const style = {
    ul: "flex space-x-0 mt-0 border-b border-gray-300",
    li: "text-gray-900 pb-2 px-3 xl:px-4 lg:px-4 hover:opacity-70 text-sm xl:text-base cursor-pointer whitespace-nowrap",
    selected:
      "cursor-default border-b-4 border-blue-700 text-sm xl:text-base text-blue-700 pb-2 px-3 xl:px-4 lg:px-4 font-medium whitespace-nowrap ",
  };

  useEffect(() => {
    async function getProfileActive() {
      const response = await getAthleteProfile(apiInstance, userId);
      setLocalProfile(response);
    }
    if (isolateProfile) {
      getProfileActive();
    } else {
      dispatch(setProfileAction(apiInstance, userId));
    }
    dispatch(
      setScoreConfig({
        showOffers: showOffers,
        showTrackittScore: showTrackittScore,
        showPositionScore: showPositionScore,
        searchEvent: searchEvent,
        eventId: eventId,
        showProfile: showProfile,
        showRank: showRank,
        showLikes: showLikes,
        sharedProfile: sharedProfile,
      }),
    );
    setMove(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, apiInstance, userId]);
  const activeProfile = isolateProfile ? localProfile : profile;
  if (!activeProfile || loading)
    return (
      <div className="my-10 lg:w-full xl:h-full">
        <LoadingSpinner />
      </div>
    );

  return (
    move === 1 &&
    activeProfile &&
    !loading && (
      <div className="h-full">
        <div className="bg-white shadow-lg p-4 border-gray-300 h-full relative">
          <div className="top-0 bg-white z-20">
            <AthleteInformation
              sharedProfile={sharedProfile}
              profile={activeProfile}
              userId={userId}
              onFavoritesRefresh={onFavoritesRefresh}
            />
            <ul className={style.ul}>
              {!isLocked && (
                <li
                  className={tab === 1 ? style.selected : style.li}
                  onClick={() => setTab(1)}
                >
                  Combine Results
                </li>
              )}
              {hasSubscription && !isLocked && showProfile && (
                <>
                  <li
                    className={tab === 2 ? style.selected : style.li}
                    onClick={() => setTab(2)}
                  >
                    Position Drills
                  </li>
                  <li
                    className={tab === 3 ? style.selected : style.li}
                    onClick={() => setTab(3)}
                  >
                    Additional Info
                  </li>
                </>
              )}
            </ul>
            {tab === 1 && !isLocked && buttons.combine.tab && (
              <AthleteCombineButtons profile={activeProfile} />
            )}
          </div>
          {tab === 1 && !isLocked && (
            <div
              className="mt-0 px-4 -m-4 overflow-y-auto pb-12"
              style={{ height: `${adjustedHeight}px` }}
            >
              <div className="pt-0 mb-10">
                <div className="flex justify-between items-center">
                  <h1 className="text-xl font-medium">Measurements </h1>
                  <button
                    type="button"
                    className="flex items-center bg-white border hover:bg-gray-200 border-gray-300 rounded-md text-sm px-3 py-1.5 cursor-pointer transition"
                    onClick={() => setShowScoreInfoModal(true)}
                  >
                    Score Info
                    <span className="pl-2">
                      <svg
                        fill="#000000"
                        width="18px"
                        height="18px"
                        viewBox="-2 0 19 19"
                        xmlns="http://www.w3.org/2000/svg"
                        className="cf-icon-svg"
                      >
                        <path d="M13.55 15.256H1.45a.554.554 0 0 1-.553-.554V3.168a.554.554 0 1 1 1.108 0v10.98h11.544a.554.554 0 0 1 0 1.108zM3.121 13.02V6.888a.476.476 0 0 1 .475-.475h.786a.476.476 0 0 1 .475.475v6.132zm2.785 0V3.507a.476.476 0 0 1 .475-.475h.786a.476.476 0 0 1 .475.475v9.513zm2.785 0V6.888a.476.476 0 0 1 .475-.475h.786a.476.476 0 0 1 .475.475v6.132zm2.786 0v-2.753a.476.476 0 0 1 .475-.475h.785a.476.476 0 0 1 .475.475v2.753z" />
                      </svg>
                    </span>
                  </button>
                </div>
                <AthleteMeasurements profile={activeProfile} />
                <h1 className="text-xl font-medium mt-6">Athletic Score</h1>
                <CombineResults profile={activeProfile} />
              </div>
            </div>
          )}
          {hasSubscription && !isLocked && (
            <>
              {tab === 2 && (
                <div
                  className="mt-0 px-4 -m-4 overflow-y-auto pb-12"
                  style={{ height: `${adjustedHeight + 80}px` }}
                >
                  {" "}
                  <PositionDrills userId={userId} isActive={tab === 2} />
                </div>
              )}
              {tab === 3 && (
                <div
                  className="mt-0 px-4 -m-4 overflow-y-auto pb-12"
                  style={{ height: `${adjustedHeight + 80}px` }}
                >
                  <AthleteAdditionalInfo userId={userId} />
                </div>
              )}
            </>
          )}

          <InfoModal
            isOpen={showScoreInfoModal}
            title={SCORE_INFO_TITLE}
            description={scoreInfoDescription}
            onClose={() => setShowScoreInfoModal(false)}
          />
        </div>
      </div>
    )
  );
}
